import { useState, useEffect } from 'react';
import { X, Loader2, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { usePaymentMutations } from '../hooks';
import type { Payment } from '../types';

interface AttachPaymentBillDialogProps {
  payment: Payment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Allocate an advance / unallocated payment to one of its contract's UNPAID bills
 * (TZ Lot 1 §7.3 "разнесение"). Bills are scoped to the payment's contract and to
 * the UNPAID state server-side; the backend re-validates everything.
 */
export function AttachPaymentBillDialog({
  payment,
  open,
  onOpenChange,
}: AttachPaymentBillDialogProps) {
  const { t } = useTranslation();
  const { attachBillMutation } = usePaymentMutations();
  const [billId, setBillId] = useState<number | undefined>(undefined);
  // Bump on each open so the bill picker always fetches fresh UNPAID bills
  // (the combobox caches its list for 5 min — a just-created bill would
  // otherwise be missing until a page reload).
  const [openNonce, setOpenNonce] = useState(0);

  useEffect(() => {
    if (open) {
      setBillId(undefined);
      setOpenNonce((n) => n + 1);
    }
  }, [open]);

  const handleAttach = () => {
    if (!billId) return;
    attachBillMutation.mutate(
      { id: payment.id, data: { billId } },
      {
        onSuccess: () => {
          onOpenChange(false);
          setBillId(undefined);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="p-6 max-w-md">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {t('attachToBill')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => onOpenChange(false)} aria-label="Close dialog">
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <p className="text-sm text-muted-foreground mt-3">
          {t('attachToBillDescription')}
        </p>

        <div className="mt-4">
          <label className="text-sm font-medium">{t('bill')} *</label>
          <div className="mt-1.5">
            <UniversalCombobox
              url="/bills"
              labelKey="id"
              valueKey="id"
              queryKey={`attach-bill-${payment.id}-${openNonce}`}
              params={{ contractId: payment.contractId, state: 'UNPAID' }}
              selectedId={billId}
              onSelectItem={(item) =>
                setBillId(item ? (item['id'] as number) : undefined)
              }
              renderLabel={(b) =>
                `#${String(b['id']).padStart(5, '0')}` +
                (b['total'] != null
                  ? ` · ${Number(b['total']).toLocaleString()} ${b['currency'] ?? ''}`.trimEnd()
                  : '')
              }
              placeholder={t('selectBill')}
            />
          </div>
        </div>

        <AlertDialogFooter className="flex justify-end gap-2 mt-6">
          <AlertDialogCancel onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            onClick={handleAttach}
            disabled={!billId || attachBillMutation.isPending}
          >
            {attachBillMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="mr-2 h-4 w-4" />
            )}
            {t('attach')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
